import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon, MagnifyingGlassIcon, PencilIcon, TrashIcon,
  ArrowDownTrayIcon, ChevronLeftIcon, ChevronRightIcon,
} from '@heroicons/react/24/outline';
import Layout from '../../components/Layout';
import { waybillAPI } from '../../api/waybillAPI';
import { exportToCSV } from '../../utils/csvExport';
import { useCompanyContext } from '../../hooks/useCompanyContext';
import { currentMonthRange } from '../../utils/dateUtils';

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-PH') : '—';
const PAGE_SIZE = 25;

export default function WaybillListPage() {
  const navigate = useNavigate();
  const { accountingClientId, contextKind } = useCompanyContext();
  const { first, last } = currentMonthRange();

  const [waybills, setWaybills]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom]         = useState(first);
  const [dateTo, setDateTo]             = useState(last);
  const [page, setPage]                 = useState(1);

  function StatusBadge({ status }) {
    const map = {
      draft:     'bg-gray-100 text-gray-600',
      active:    'bg-blue-100 text-blue-700',
      delivered: 'bg-green-100 text-green-700',
      voided:    'bg-red-100 text-red-600 line-through',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || map.draft}`}>
        {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Draft'}
      </span>
    );
  }

  useEffect(() => {
    fetchWaybills(dateFrom, dateTo);
  }, [accountingClientId, contextKind]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchWaybills = async (from = dateFrom, to = dateTo) => {
    setLoading(true);
    try {
      const params = { shipmentType: 'freightforwarding' };
      if (from) params.dateFrom = from;
      if (to)   params.dateTo   = to;
      const res = await waybillAPI.getAll(params);
      setWaybills(res.data || []);
      setPage(1);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this waybill?')) return;
    try {
      await waybillAPI.delete(id);
      setWaybills((prev) => prev.filter((w) => w._id !== id));
    } catch {
      alert('Failed to delete waybill.');
    }
  };

  // Client-side filter (search + status)
  const filtered = waybills.filter((w) => {
    if (statusFilter && w.status !== statusFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    const customerName = (
      w.CustomerId?.CustomerName ||
      w.CustomerId?.name         ||
      w.CustomerName             || ''
    ).toLowerCase();
    return (
      w.SerialNo?.toLowerCase().includes(q)        ||
      w.referenceNumber?.toLowerCase().includes(q) ||
      w.Shipper?.toLowerCase().includes(q)         ||
      customerName.includes(q)                     ||
      w.Origin?.toLowerCase().includes(q)          ||
      w.Destination?.toLowerCase().includes(q)
    );
  });

  // Reset to page 1 when filters change
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const paginated  = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleExportCSV = () => {
    const rows = filtered.map((w) => ({
      'Serial No.':  w.SerialNo || '—',
      'Ref No.':     w.referenceNumber || '—',
      'Date':        fmt(w.TransactionDate),
      'Shipper':     w.Shipper || '—',
      'Consignee':   w.CustomerId?.CustomerName || w.CustomerId?.name || w.CustomerName || '—',
      'Origin':      w.Origin || '—',
      'Destination': w.Destination || '—',
      'Vessel':      w.Vessel || '—',
      'Amount':      w.totalAmount != null ? w.totalAmount.toFixed(2) : '0.00',
      'Delivered':   w.IsDelivered ? 'Yes' : 'No',
      'Status':      w.status || 'draft',
    }));
    const from = dateFrom || 'all';
    const to   = dateTo   || 'all';
    exportToCSV(rows, `waybills-${from}-to-${to}.csv`);
  };

  return (
    <Layout pageTitle="Waybill — Freight Forwarding">
      <div className="space-y-4">
        {/* Toolbar */}
        <div className="bg-white rounded-lg shadow px-4 py-3 flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Date From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Date To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">All Statuses</option>
              {['draft', 'active', 'delivered', 'voided'].map((s) => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-gray-500 mb-1">Search</label>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Serial, ref no., shipper, consignee…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm w-full focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={() => fetchWaybills(dateFrom, dateTo)}
              disabled={loading}
              className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 disabled:opacity-50"
            >
              {loading ? 'Loading…' : 'Apply'}
            </button>
            <button
              onClick={() => {
                // Drops the date range too, so users can search a ref no. without knowing its date.
                setDateFrom(''); setDateTo(''); setStatusFilter(''); setSearch('');
                fetchWaybills('', '');
              }}
              className="px-4 py-2 border border-gray-300 text-gray-600 text-sm rounded-lg hover:bg-gray-50"
            >
              Clear
            </button>
            <button
              onClick={handleExportCSV}
              disabled={filtered.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Export CSV
            </button>
            <button
              onClick={() => navigate('/billing/waybill/add')}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700"
            >
              <PlusIcon className="h-4 w-4" />
              Add New Waybill
            </button>
          </div>
        </div>

        {/* Summary + pagination info */}
        {!loading && (
          <div className="flex items-center justify-between px-1 text-xs text-gray-500">
            <span>
              Showing{' '}
              <span className="font-semibold text-gray-700">
                {filtered.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)}
              </span>
              {' '}of{' '}
              <span className="font-semibold text-gray-700">{filtered.length}</span> waybill(s)
            </span>
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-40"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>
                <span className="px-2">Page {safePage} of {totalPages}</span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-40"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['Serial No.', 'Ref No.', 'Date', 'Shipper', 'Consignee', 'Origin', 'Destination', 'Vessel', 'Delivered', 'Status', 'Actions'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                Array.from({ length: PAGE_SIZE }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 11 }).map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-4 bg-gray-200 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : paginated.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-10 text-center text-gray-400">
                    No waybills found.{' '}
                    <button onClick={() => navigate('/billing/waybill/add')} className="text-teal-600 underline">
                      Add your first waybill.
                    </button>
                  </td>
                </tr>
              ) : (
                paginated.map((w) => (
                  <tr key={w._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{w.SerialNo || '—'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{w.referenceNumber || '—'}</td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmt(w.TransactionDate)}</td>
                    <td className="px-4 py-3 text-gray-600">{w.Shipper || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {w.CustomerId?.CustomerName || w.CustomerId?.name || w.CustomerName || w.customerName || '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{w.Origin || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{w.Destination || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{w.Vessel || '—'}</td>
                    <td className="px-4 py-3">
                      {w.IsDelivered ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">Delivered</span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">Pending</span>
                      )}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={w.status} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => navigate(`/billing/waybill/${w._id}/edit`)}
                          className="p-1.5 rounded hover:bg-teal-50 text-teal-600"
                          title="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(w._id)}
                          className="p-1.5 rounded hover:bg-red-50 text-red-500"
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Bottom pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => setPage(1)}
              disabled={safePage === 1}
              className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            >
              First
            </button>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage === 1}
              className="p-1.5 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((n) => n === 1 || n === totalPages || Math.abs(n - safePage) <= 2)
              .reduce((acc, n, i, arr) => {
                if (i > 0 && n - arr[i - 1] > 1) acc.push('…');
                acc.push(n);
                return acc;
              }, [])
              .map((n, i) =>
                n === '…' ? (
                  <span key={`ellipsis-${i}`} className="px-2 text-gray-400">…</span>
                ) : (
                  <button
                    key={n}
                    onClick={() => setPage(n)}
                    className={`px-3 py-1.5 text-xs border rounded-lg ${
                      n === safePage
                        ? 'bg-teal-600 text-white border-teal-600'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {n}
                  </button>
                )
              )}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              className="p-1.5 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={safePage === totalPages}
              className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            >
              Last
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}
